import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import BankAccountService from "services/Setup/BankAccount/BankAccountService";
import { notification } from "antd";

export const initialState = {
  loading: false,
  bankAccountList: [],
  bankAccountCount: 0,
  bankAccountApprovalList: [],
  bankAccountApprovalCount: 0,
  bankAccountPairingList: [],
  bankAccountPairingCount: 0,
  selectedBankAccount: null,
};


export const getBankAccountList = createAsyncThunk(
  "bankAccount/getBankAccountList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.getBankAccounts(params);

      const payload = response?.data;

      // Paginated shape: { pageIndex, pageSize, count, data: [...] }
      if (payload && Array.isArray(payload.data)) {
        return { list: payload.data, count: payload.count ?? payload.data.length };
      }
      // Fallbacks (older shapes)
      if (Array.isArray(payload)) {
        return { list: payload, count: payload.length };
      }
      if (Array.isArray(payload?.result)) {
        return { list: payload.result, count: payload.result.length };
      }

      return { list: [], count: 0 };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Normalizes the paginated { pageIndex, pageSize, count, data } response.
const toListAndCount = (response) => {
  const payload = response?.data;
  if (payload && Array.isArray(payload.data)) {
    return { list: payload.data, count: payload.count ?? payload.data.length };
  }
  if (Array.isArray(payload)) return { list: payload, count: payload.length };
  if (Array.isArray(payload?.result)) return { list: payload.result, count: payload.result.length };
  return { list: [], count: 0 };
};

export const getBankAccountPairingList = createAsyncThunk(
  "bankAccount/getBankAccountPairingList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.getBankAccountsPairing(params);
      return toListAndCount(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getBankAccountApprovalList = createAsyncThunk(
  "bankAccount/getBankAccountApprovalList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.getBankAccountsApproval(params);
      return toListAndCount(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getBankAccountById = createAsyncThunk(
  "bankAccount/getBankAccountById",
  async (bankAccountId, { rejectWithValue }) => {
    try {
      // Dedicated unfiltered detail endpoint — returns the record regardless of
      // its flag/status (needed by the approval-detail screen).
      const response = await BankAccountService.getBankAccountByBankAccountId(bankAccountId);

      const payload = response?.data;
      // Detail endpoint returns the view model object directly.
      if (payload && !Array.isArray(payload) && payload.bankAccountId) return payload;
      // Fallbacks in case a list shape is returned.
      if (Array.isArray(payload)) return payload.find((x) => x.bankAccountId === bankAccountId) ?? null;
      if (Array.isArray(payload?.data)) return payload.data.find((x) => x.bankAccountId === bankAccountId) ?? null;

      return null;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addBankAccount = createAsyncThunk(
  "bankAccount/addBankAccount",
  async (userInput, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.addBankAccount(userInput);

      if (response.status === 200) {
        const data = response.data;
        if (data.status === "Success") {
          notification.success({
            message: data.status,
            description: data.message,
            duration: 3,
          });
        } else {
          notification.error({
            message: data.status,
            description: data.message,
            duration: 3,
          });
        }
        return data;
      } else {
        notification.error({
          message: "Error",
          description: "Unexpected response!",
          duration: 3,
        });
        return rejectWithValue({
          status: "Error",
          response: response?.data?.message || "Unexpected response!",
        });
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Unexpected error!");
    }
  }
);

export const updateBankAccount = createAsyncThunk(
  "bankAccount/updateBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.updateBankAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
          duration: 3,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: error.response?.status,
        description: error.response?.statusText,
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteBankAccount = createAsyncThunk(
  "bankAccount/deleteBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.deleteBankAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Delete failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const approveBankAccount = createAsyncThunk(
  "bankAccount/approveBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.approveBankAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Approve failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const rejectBankAccount = createAsyncThunk(
  "bankAccount/rejectBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.rejectBankAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Reject failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const returnBankAccount = createAsyncThunk(
  "bankAccount/returnBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.returnBankAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Return failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const requestBankAccount = createAsyncThunk(
  "bankAccount/requestBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.requestBankAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Request failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

const BankAccountSlice = createSlice({
  name: "bankAccount",
  initialState,
  reducers: {
    clearSelectedBankAccount: (state) => {
      state.selectedBankAccount = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBankAccountList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBankAccountList.fulfilled, (state, action) => {
        state.loading = false;
        state.bankAccountList = action.payload.list;
        state.bankAccountCount = action.payload.count;
      })
      .addCase(getBankAccountList.rejected, (state) => {
        state.loading = false;
      })
      // PAIRING (head-office only)
      .addCase(getBankAccountPairingList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBankAccountPairingList.fulfilled, (state, action) => {
        state.loading = false;
        state.bankAccountPairingList = action.payload.list;
        state.bankAccountPairingCount = action.payload.count;
      })
      .addCase(getBankAccountPairingList.rejected, (state) => {
        state.loading = false;
      })
      // APPROVAL (awaiting approval)
      .addCase(getBankAccountApprovalList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBankAccountApprovalList.fulfilled, (state, action) => {
        state.loading = false;
        state.bankAccountApprovalList = action.payload.list;
        state.bankAccountApprovalCount = action.payload.count;
      })
      .addCase(getBankAccountApprovalList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getBankAccountById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBankAccountById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBankAccount = action.payload;
      })
      .addCase(getBankAccountById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(addBankAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload;
        state.showMessage = true;
        state.message = action.payload?.message;
        state.statusRespon = 200;
        state.redirect = "/";
      })
      .addCase(addBankAccount.rejected, (state, action) => {
        state.loading = false;
        state.result = action.payload;
        state.showMessage = true;
        state.statusRespon = "Error";
        state.message = action.payload?.message || "Failed to save data.";
      })
      .addCase(updateBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBankAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateBankAccount.rejected, (state) => {
        state.loading = false;
      })

      .addCase(deleteBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteBankAccount.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteBankAccount.rejected, (state) => {
        state.loading = false;
      })
      // APPROVE
      .addCase(approveBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveBankAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveBankAccount.rejected, (state) => {
        state.loading = false;
      })

      // REJECT
      .addCase(rejectBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(rejectBankAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(rejectBankAccount.rejected, (state) => {
        state.loading = false;
      })

      // RETURN
      .addCase(returnBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(returnBankAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(returnBankAccount.rejected, (state) => {
        state.loading = false;
      })

      // REQUEST
      .addCase(requestBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestBankAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestBankAccount.rejected, (state) => {
        state.loading = false;
      })
  },
});

export const { clearSelectedBankAccount } = BankAccountSlice.actions;

export default BankAccountSlice.reducer;
